/**
 * UpdateBanner — converge long-lived sessions onto the latest deploy.
 *
 * The app is a single long-lived page; a tab left open across a deploy keeps running the OLD bundle
 * until it reloads. That makes features look "different / old version" for whoever didn't reload
 * (it has nothing to do with role or permissions). This polls the served index.html for a new
 * hashed entry script and, when the deploy changes, shows a subtle "Reload" prompt so everyone ends
 * up on the same current build.
 *
 * Mounted once in OutlineView. No-ops in dev (no hashed entry) and never auto-reloads (won't
 * interrupt work) — the user clicks Reload when ready.
 */

import { useEffect, useState, useRef } from 'react'

function entryHash(html) { const m = (html || '').match(/assets\/index-[\w-]+\.js/); return m ? m[0] : '' }
function currentEntry() {
  const src = Array.from(document.querySelectorAll('script[src]'))
    .map(s => s.getAttribute('src') || '')
    .find(s => /assets\/index-[\w-]+\.js/.test(s)) || ''
  return entryHash(src)
}

export default function UpdateBanner() {
  const [show, setShow] = useState(false)
  const [note, setNote] = useState('')        // the new deploy's commit subject — a hint at what changed
  const baseRef = useRef(currentEntry())

  useEffect(() => {
    if (!baseRef.current) return        // dev server / no hashed entry → nothing to compare
    let stopped = false
    async function check() {
      if (stopped) return
      try {
        const r = await fetch('/?_v=' + Date.now(), { cache: 'no-store' })
        if (!r.ok) return
        const latest = entryHash(await r.text())
        if (latest && latest !== baseRef.current) {
          stopped = true
          // Pull the new deploy's commit message (what Vercel shows for the deployment) for a hint.
          try {
            const vr = await fetch('/version.json?_v=' + Date.now(), { cache: 'no-store' })
            if (vr.ok) { const v = await vr.json(); if (v && v.message) setNote(v.message) }
          } catch (_) { /* version.json optional — fall back to the generic message */ }
          setShow(true)
        }
      } catch (_) { /* offline / transient — try again next tick */ }
    }
    const id = setInterval(check, 10 * 60 * 1000)               // every 10 min
    const onVis = () => { if (document.visibilityState === 'visible') check() }  // and when the tab refocuses
    document.addEventListener('visibilitychange', onVis)
    const t = setTimeout(check, 15000)                          // one check shortly after load
    return () => { stopped = true; clearInterval(id); clearTimeout(t); document.removeEventListener('visibilitychange', onVis) }
  }, [])

  if (!show) return null
  return (
    <div style={banner}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <span style={{ fontSize: 12.5, color: 'var(--aj-text)', fontWeight: 500 }}>A newer version of AlderCortex is available.</span>
        {note && (
          <span style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={note}>
            Latest change: {note}
          </span>
        )}
      </div>
      <button style={btn} onClick={() => window.location.reload()}>Reload</button>
      <button style={dismiss} title="Dismiss" onClick={() => setShow(false)}>×</button>
    </div>
  )
}

const banner = {
  position: 'fixed', bottom: 16, left: 16, zIndex: 1000002,
  display: 'flex', alignItems: 'center', gap: 12,
  background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 10,
  boxShadow: '0 10px 30px rgba(0,0,0,0.18)', padding: '10px 14px', fontFamily: '"DM Sans",sans-serif',
  maxWidth: 'min(440px, calc(100vw - 32px))',
}
const btn = { background: '#07272D', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif' }
const dismiss = { background: 'none', border: 'none', color: 'var(--aj-text-muted)', fontSize: 17, lineHeight: 1, cursor: 'pointer' }
